import { ResumeState } from '@/models/resume-state'
import { Center, Notification } from '@tempots/beatui'
import {
  attr,
  delaySignal,
  effectOf,
  Empty,
  html,
  OneOfType,
  prop,
  Signal,
  TNode,
  Value,
  When,
} from '@tempots/dom'
import { ProgressPanel } from './progress-panel'
import { PDFPreview } from './pdf-preview'
import { AsyncResult } from '@tempots/std'
import { AsyncResultView, ticker } from '@tempots/ui'
import { EphimeralService } from '@/utils/ephimeral'
import { LatestResume } from '@/models/latest-resume'

export class ResumeExpiredError extends Error {
  constructor(message = 'Resume expired') {
    super(message)
    this.name = 'ResumeExpiredError'
  }
}

/**
 * Result of processing a resume for preview. `missingFonts` lists fonts the
 * converter had to substitute (layout may differ from the original document);
 * a warning is shown so the uploader can re-export as PDF if fidelity matters.
 */
export type ProcessedResumePreview = {
  url: string
  missingFonts?: string[]
}

export type ResumePDFPreviewOptions = {
  state: Signal<ResumeState>
  tick?: Signal<number>
  notAsked?: () => TNode
  noDownload?: Value<boolean>
  getUrl: Value<(hash: string) => Promise<ProcessedResumePreview>>
  getDefaultUrl?: Value<() => Promise<string>>
  autodownload?: Value<boolean>
  storeLatestResume?: boolean
  onDownload?: () => void
  agencyName: Value<string | undefined>
  /** Custom failure view (e.g. friendly plan-limit upsell). */
  renderFailure?: (error: Signal<string>) => TNode
}

function DisplayError({
  title,
  error,
}: {
  title: Value<string>
  error: TNode
}) {
  return Center(
    {},
    html.div(
      attr.class('p-8'),
      Notification(
        {
          color: 'danger',
          title,
        },
        error
      )
    )
  )
}

type PreviewSuccess = {
  url: string
  filename: string
  missingFonts?: string[]
}

function MissingFontsWarning(fonts: Signal<string[] | undefined>) {
  return html.div(
    attr.class('shrink-0 p-2'),
    Notification(
      {
        color: 'warning',
        title: 'Some fonts were substituted',
      },
      fonts.map(
        f =>
          `This document uses fonts we could not match exactly (${(f ?? []).join(', ')}), so the layout may differ from the original. For an exact match, export the original document as a PDF and upload that instead.`
      )
    )
  )
}

function StateView({
  state,
  autodownload,
  noDownload,
  onDownload,
  agencyName,
  renderFailure,
}: {
  state: Signal<AsyncResult<PreviewSuccess, string>>
  autodownload?: Value<boolean>
  noDownload?: Value<boolean>
  onDownload?: () => void
  agencyName: Value<string | undefined>
  renderFailure?: (error: Signal<string>) => TNode
}) {
  return AsyncResultView(state, {
    loading: () => ProgressPanel({ value: { title: 'Processing...' } }),
    success: res => {
      return html.div(
        attr.class('h-full flex flex-col'),
        When(
          res.map(v => (v.missingFonts?.length ?? 0) > 0),
          () => MissingFontsWarning(res.$.missingFonts)
        ),
        html.div(
          attr.class('flex-1 min-h-0'),
          PDFPreview({
            url: res.$.url,
            autodownload,
            suffix: Value.map(agencyName, name => ` - ${name ?? 'Updated'}`),
            filename: res.$.filename,
            noDownload,
            onDownload,
          })
        )
      )
    },
    failure: error =>
      renderFailure
        ? renderFailure(error)
        : DisplayError({
            title: 'Failed to process resume',
            error,
          }),
  })
}

function debounceTicker(passedTick?: Signal<number>) {
  const tick = passedTick != null ? passedTick.map(v => v) : ticker()
  return delaySignal(tick, 750)
}

export function ResumePDFPreview({
  state,
  tick: passedTick,
  notAsked,
  getUrl,
  getDefaultUrl,
  autodownload,
  noDownload = false,
  storeLatestResume = true,
  onDownload,
  agencyName,
  renderFailure,
}: ResumePDFPreviewOptions) {
  const tick = debounceTicker(passedTick)
  return html.div(
    attr.class('bg-gray-100 h-full overflow-hidden'),
    OneOfType(state, {
      notAsked: notAsked ?? (() => Empty),
      uploading: () =>
        ProgressPanel({
          value: { title: 'Uploading file...' },
        }),
      default: () => {
        return When(
          getDefaultUrl != null,
          () => {
            const state = prop<AsyncResult<PreviewSuccess, string>>(
              AsyncResult.notAsked
            )
            effectOf(
              getDefaultUrl,
              tick
            )(async fn => {
              state.set(AsyncResult.loading())
              try {
                const url = await fn!()
                state.set(AsyncResult.success({ url, filename: 'sample.pdf' }))
              } catch (error) {
                state.set(AsyncResult.failure((error as Error).message))
              }
            })
            return StateView({
              state,
              autodownload,
              noDownload,
              onDownload,
              agencyName,
              renderFailure,
            })
          },
          notAsked ?? (() => Empty)
        )
      },
      uploaded: res => {
        const state = prop<AsyncResult<PreviewSuccess, string>>(
          AsyncResult.notAsked
        )
        effectOf(
          res.$.hash,
          getUrl,
          tick
        )(async (hash, fn) => {
          state.set(AsyncResult.loading())
          try {
            const { url, missingFonts } = await fn(hash)
            state.set(
              AsyncResult.success({
                url,
                filename: res.value.filename,
                missingFonts,
              })
            )
          } catch (error) {
            if (error instanceof ResumeExpiredError) {
              state.set(AsyncResult.notAsked)
              return
            }
            state.set(AsyncResult.failure((error as Error).message))
          }
        })
        if (storeLatestResume) {
          EphimeralService.store(
            LatestResume.make(res.value.hash, res.value.filename)
          )
        }
        return StateView({
          state,
          autodownload,
          noDownload,
          onDownload,
          agencyName,
          renderFailure,
        })
      },
      converting: () =>
        ProgressPanel({
          value: { title: 'Converting to PDF...' },
        }),
      error: res =>
        Center(
          {},
          html.div(
            html.h2(
              attr.class('text-lg font-bold text-red-500'),
              'Failed to upload resume'
            ),
            html.p(attr.class('text-sm text-red-500'), res.$.error)
          )
        ),
    })
  )
}
